import { dateToCalendarStateIndex } from '@/helpers/calendarHelper';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import {
  AvailabilityException,
  WorkingPatternDay,
  useEmployeeWorkingPatternQuery,
  useGetMeQuery,
  useGetRosteredStaffInDateRangeQuery,
  useMyAvailabilityExceptionsQuery
} from '@/services/gql/graphql.generated';
import { useLocationService } from '@/services/location/LocationService';
import { strings } from '@/services/translation/strings';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmployeeSchedule, EmployeeScheduleDay } from './schedule.types';
import { useBuildSchedule } from './useBuildSchedule';

export const useMySchedule = (startDate: Date, endDate?: Date) => {
  const resolvedEndDate = endDate ?? startDate;

  const [{ data: myData }] = useGetMeQuery();
  const userId = myData!.myEmployee?.identityId;

  const [{ data: myAvailabilityData }, refetchExceptions] = useMyAvailabilityExceptionsQuery({
    variables: {
      dateRange: { start: dateToApiFormat(startDate), end: dateToApiFormat(resolvedEndDate) }
    },
    requestPolicy: 'network-only'
  });
  const exceptions = myAvailabilityData?.myAvailabilityExceptions?.exceptions as AvailabilityException[];

  const [{ data: myWorkingPatternData }, refetchPatterns] = useEmployeeWorkingPatternQuery({
    requestPolicy: 'network-only'
  });

  const workingPatternPeriods = myWorkingPatternData?.myWorkingPatterns?.periods || [];

  if (workingPatternPeriods.length === 0) {
    throw Error('Employee working pattern data should be setup');
  }

  const patterns = workingPatternPeriods[0].patterns as WorkingPatternDay[];
  if (!patterns) {
    throw Error('Patterns should be defined.');
  }

  const locationId = useLocationService(state => state.selectedLocation?.id);

  if (!locationId) {
    throw Error('Location should be selected to show calendar');
  }

  const [{ data: rosterData }, refetchRosteredStaff] = useGetRosteredStaffInDateRangeQuery({
    variables: {
      startDate: dateToApiFormat(startDate),
      endDate: dateToApiFormat(resolvedEndDate),
      locationId
    },
    requestPolicy: 'cache-and-network'
  });

  const rosteredStaffInDateRange = rosterData?.rosteredStaffInDateRange || [];

  const rosteredStaff = useMemo(
    () => rosteredStaffInDateRange.filter(s => s.identityId === userId),
    [rosteredStaffInDateRange, userId]
  );

  const [fetching, setFetching] = useState<boolean>(false);
  const refetch = useCallback(async () => {
    setFetching(true);
    await refetchExceptions({ requestPolicy: 'network-only' });
    await refetchPatterns({ requestPolicy: 'network-only' });
    await refetchRosteredStaff({ requestPolicy: 'network-only' });
    setFetching(false);
  }, [refetchExceptions, refetchPatterns]);

  const buildSchedule = useBuildSchedule(startDate, resolvedEndDate);

  const builtSchedule = useMemo(
    () => buildSchedule(patterns, exceptions, rosteredStaff),
    [buildSchedule, patterns, exceptions, rosteredStaff]
  );

  const prevScheduleRef = useRef<EmployeeSchedule | null>(null);

  const [schedule, setSchedule] = useState<EmployeeSchedule>(() => builtSchedule);
  useEffect(() => {
    const hasChanged = JSON.stringify(prevScheduleRef.current) !== JSON.stringify(builtSchedule);
    if (hasChanged) {
      prevScheduleRef.current = builtSchedule;
      setSchedule(builtSchedule);
    }
  }, [builtSchedule]);

  const { registerConfirmation } = useConfirm(state => ({ registerConfirmation: state.registerForConfirmation }));

  const setScheduleDay = useCallback(
    (scheduleDay: EmployeeScheduleDay) => {
      setSchedule(currentSchedule => ({
        ...currentSchedule,
        [dateToCalendarStateIndex(scheduleDay.date)]: scheduleDay
      }));
      registerConfirmation(strings.ess.availability.saveConfirm);
    },
    [registerConfirmation]
  );

  return { schedule, setScheduleDay, refetch, fetching };
};
